import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  container: {
    marginBottom: 10,
  },
  competencyGrid: {
    flexDirection: 'column',
    gap: 4,
    alignItems: 'flex-start',
    padding: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  phaseGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 1.2,
    marginRight: 0,
    marginBottom: 5,
    width: '100%',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    margin: 0.7,
  },
  legend: {
    marginTop: 6,
    padding: 4,
    backgroundColor: '#f5f5f5',
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 2,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 1,
  },
  legendDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginRight: 2,
  },
  legendText: {
    fontSize: 5,
  },
  emptyMessage: {
    fontSize: 8,
    textAlign: 'center',
    color: '#888888',
    padding: 10,
  }
});

// Category colors for the dots
const categoryColors = {
  skills: '#4CAF50',      // Green
  knowledge: '#2196F3',   // Blue  
  mindset: '#FF9800',     // Orange
  instruments: '#9C27B0', // Purple
};

// Fixed hex shades per category and frequency level
const categoryFrequencyShades = {
  skills: {
    low: '#b6dab8',
    medium: '#71ae74',
    high: '#0E5A24',
  },
  knowledge: {
    low: '#a6d0f2',
    medium: '#58a0d9',
    high: '#0B3C73',
  },
  mindset: {
    low: '#f7d29b',
    medium: '#dfa04b',
    high: '#7A3F00',
  },
  instruments: {
    low: '#d0a6d7',
    medium: '#a75fb3',
    high: '#3A0A4A',
  },
};


// Helper function to get visual styles based on frequency (simplified for PDF)
const getFrequencyStyles = (competency, categoryColor) => {
  if (!competency.isSelected) {
    return {
      backgroundColor: '#ccc',
    };
  }

  const frequency = Math.max(0, Math.min(100, competency.frequency || 0));
  const categoryKey = competency.category;
  const shades = categoryFrequencyShades[categoryKey] || categoryFrequencyShades.skills;
  const level = frequency >= 67 ? 'high' : frequency >= 34 ? 'medium' : 'low';
  const shade = shades[level];

  return {
    backgroundColor: shade,
  };
};

const CohortCinemaMapPDF = ({ cohortResponses, allCompetenciesData }) => {
  // Get competency IDs that were selected in this cohort
  const selectedCompetencyIds = useMemo(() => {
    if (!cohortResponses?.competency_counts) return new Set();
    return new Set(cohortResponses.competency_counts.map(item => item.competency_id));
  }, [cohortResponses]);

  // Get competency count map and calculate frequencies
  const competencyData = useMemo(() => {
    if (!cohortResponses?.competency_counts || !cohortResponses?.session_count) {
      return { countMap: new Map(), frequencyMap: new Map() };
    }
    
    const countMap = new Map();
    const frequencyMap = new Map();
    const totalParticipants = cohortResponses.session_count;
    
    cohortResponses.competency_counts.forEach(item => {
      const count = item.count;
      const percentage = (count / totalParticipants) * 100;
      
      countMap.set(item.competency_id, count);
      frequencyMap.set(item.competency_id, percentage);
    });
    
    return { countMap, frequencyMap };
  }, [cohortResponses]);

  // Process all competencies and group by phase
  const competenciesByPhase = useMemo(() => {
    if (!allCompetenciesData) return {};

    const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
    const grouped = {};

    // Initialize each phase
    phases.forEach(phase => {
      grouped[phase] = [];
    });

    // Group competencies by phase
    allCompetenciesData.forEach(comp => {
      const phase = comp.abilities?.phase || 'Unknown';
      const competency = {
        id: comp.id,
        name: comp.name,
        category: comp.category,
        phase: phase,
        ability: comp.abilities?.name || 'Unknown',
        isSelected: selectedCompetencyIds.has(comp.id),
        count: competencyData.countMap.get(comp.id) || 0,
        frequency: competencyData.frequencyMap.get(comp.id) || 0
      };

      if (grouped[phase]) {
        grouped[phase].push(competency);
      } else {
        // Handle unknown phases
        if (!grouped['Unknown']) grouped['Unknown'] = [];
        grouped['Unknown'].push(competency);
      }
    });

    return grouped;
  }, [allCompetenciesData, selectedCompetencyIds, competencyData]);

  if (!allCompetenciesData || allCompetenciesData.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyMessage}>
          No competencies available in the system
        </Text>
      </View>
    );
  }

  if (!cohortResponses || !cohortResponses.competency_counts || cohortResponses.competency_counts.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyMessage}>
          No competencies selected in this cohort yet
        </Text>
      </View>
    );
  }

  const renderDots = (competencies) => {
    return competencies.map((competency, index) => {
      const categoryColor = categoryColors[competency.category] || '#666666';
      const frequencyStyles = getFrequencyStyles(competency, categoryColor);
      
      return (
        <View
          key={`${competency.id}-${index}`}
          style={[
            styles.dot,
            frequencyStyles
          ]}
        />
      );
    });
  };

  return (
    <View style={styles.container}>
      {/* Competencies grouped by phases with spacing */}
      <View style={styles.competencyGrid}>
        {Object.entries(competenciesByPhase).map(([phase, competencies]) => {
          if (!competencies || competencies.length === 0) return null;
          
          return (
            <View key={phase} style={styles.phaseGroup}>
              {renderDots(competencies)}
            </View>
          );
        })}
      </View>

      {/* Compact legend */}
      <View style={styles.legend}>
        <View style={styles.legendRow}>
          {/* Category colors */}
          {Object.entries(categoryColors).map(([category, color]) => (
            <View key={category} style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: color }]} />
              <Text style={styles.legendText}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Text>
            </View>
          ))}
          
          {/* Frequency indicators (simple discrete hex shades) */}
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: categoryFrequencyShades.skills.low }]} />
            <Text style={styles.legendText}>Low</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: categoryFrequencyShades.skills.medium }]} />
            <Text style={styles.legendText}>Medium</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: categoryFrequencyShades.skills.high }]} />
            <Text style={styles.legendText}>High</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: '#ccc' }]} />
            <Text style={styles.legendText}>Not selected</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export default CohortCinemaMapPDF;
